/**
 * API base URL. Empty string means requests use relative paths and go through
 * the same origin (Cloudflare Pages -> Worker proxy). This avoids cross-origin
 * and DNS issues on restricted networks (e.g. some tablets/mobile networks that
 * cannot reach the raw *.workers.dev domain directly).
 *
 * In local development Vite proxies /api and /media to the Worker.
 */
export const API_BASE = "";

/**
 * API fetch wrapper that automatically injects the admin token
 * from localStorage as a Bearer Authorization header.
 * Replaces EdgeSpark's client.api.fetch which doesn't work with
 * our custom RBAC session token system.
 */
export async function apiFetch(
  path: string,
  options: RequestInit & { skipAdminAuth?: boolean } = {}
): Promise<Response> {
  let token: string | null = null;
  if (!options.skipAdminAuth) {
    try {
      token = localStorage.getItem("admin_token");
    } catch (_) {
      // Some in-app browsers block localStorage access; continue without token
      token = null;
    }
  }
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // 全局強制 UTF-8，避免中文在部分環境被誤解為 latin1 導致亂碼
  if (options.body && typeof options.body === 'string') {
    const ct = headers['Content-Type'] || headers['content-type'] || '';
    if (!ct || ct.startsWith('application/json')) {
      headers['Content-Type'] = 'application/json; charset=utf-8';
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });
  // Auto-redirect to login on 401 (token expired or invalid)
  if (res.status === 401 && token && !path.includes('/auth/login')) {
    try {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
    } catch (_) {
      // Ignore in-app browsers that block localStorage
    }
    // Only redirect if we're on an admin page
    if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
      window.location.href = '/admin/login';
    }
  }
  return res;
}
